import React from 'react';
import PropTypes from 'prop-types';
import { Link, useSearchParams } from 'react-router-dom';
import './JobPosting.css';

export default function JobPosting({ jobs = [], pageSize = 10}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10));
  const total = jobs.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const safePage = Math.min(page, totalPages);

  const start = (safePage - 1) * pageSize;
  const end = start + pageSize;
  const pageJobs = jobs.slice(start, end);

  const goToPage = (p) => {
    const clamped = Math.min(Math.max(1, p), totalPages);
    const next = new URLSearchParams(searchParams);
    next.set('page', String(clamped));
    setSearchParams(next);
  };

  if (total === 0) {
    return <p className="no-jobs">No positions match your criteria.</p>;
  }

  return (
    <>
      <div className="jobposting-list">
        {pageJobs.map((job, idx) => (
          <Link
            key={job._id}
            to={`/careers/jobs/${job._id}`}
            className={`jobposting-item ${idx % 2 === 0 ? 'odd' : 'even'}`}
          >
            <div className="jobposting-title">{job.title}</div>
            <div className="jobposting-location">{job.location}</div>
          </Link>
        ))}
      </div>

      {totalPages > 1 && (
        <nav className="job-pagination" role="navigation" aria-label="Jobs pagination">
          <button
            className="job-page-btn"
            onClick={() => goToPage(safePage - 1)}
            disabled={safePage <= 1}
          >
            ← Prev
          </button>

          <ul className="job-page-list">
            {Array.from({ length: totalPages }).map((_, i) => {
              const n = i + 1;
              const isActive = n === safePage;

              const show =
                n === 1 ||
                n === totalPages ||
                Math.abs(n - safePage) <= 1 ||
                (safePage <= 3 && n <= 5) ||
                (safePage >= totalPages - 2 && n >= totalPages - 4);

              if (!show) {
                if (n === 2 || n === totalPages - 1) {
                  return <li key={`e-${n}`} className="job-page-ellipsis">…</li>;
                }
                return null;
              }

              return (
                <li key={n}>
                  <button
                    className={`job-page-num ${isActive ? 'is-active' : ''}`}
                    onClick={() => goToPage(n)}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {n}
                  </button>
                </li>
              );
            })}
          </ul>

          <button
            className="job-page-btn"
            onClick={() => goToPage(safePage + 1)}
            disabled={safePage >= totalPages}
          >
            Next →
          </button>
        </nav>
      )}
    </>
  );
}

JobPosting.propTypes = {
  jobs: PropTypes.arrayOf(
    PropTypes.shape({
      _id:            PropTypes.string.isRequired,
      title:          PropTypes.string.isRequired,
      location:       PropTypes.string.isRequired,
      specialization: PropTypes.arrayOf(PropTypes.string),
    })
  ),
  /** How many jobs per page */
  pageSize: PropTypes.number,
};

JobPosting.defaultProps = {
  jobs: [],
  pageSize: 10,
};
